import type { LanguageCodeEnum } from '@/shared/config/common/constants/lang';
import { trackApiError } from '@/shared/lib/trackError';

import { createSocket, isSocketAvailable } from '../index';

import type { ICity } from '@/entities/common/types/city';
import type { SearchedDoctors } from '@/entities/doctor/model/types';
import type { SearchedMedcenters } from '@/entities/medcenter/model/types';
import type { Skill } from '@/entities/skill/model/types';

import type { SearchIndex, Socket } from '../index';

interface SearchBaseResult<T> {
  data: T[];
  query: string;
  totalHits: number;
}

/**
 * Маппинг индексов к типам
 */
type SearchIndexToType = {
  doctors: SearchBaseResult<SearchedDoctors>;
  medcenters: SearchBaseResult<SearchedMedcenters>;
  skills: SearchBaseResult<Skill>;
  cities: SearchBaseResult<ICity>;
};

/**
 * Type-safe ответ по индексам
 */
type SearchDataResponse<T extends SearchIndex[]> = {
  [K in T[number]]: SearchIndexToType[K];
};

/**
 * Тело запроса поиска
 */
interface SearchRequestBody<T extends SearchIndex[]> {
  query: string;
  indexes: T;
  language: LanguageCodeEnum;
}

/**
 * Ответ API поиска
 */
interface SearchApiResponse<T extends SearchIndex[]> {
  data: SearchDataResponse<T>;
  message: string;
}

type SearchCallback<T extends SearchIndex[]> = (response: SearchApiResponse<T>) => void;
type SearchErrorCallback = (error: unknown) => void;

interface SearchOptions<T extends SearchIndex[]> {
  onSuccess?: SearchCallback<T>;
  onError?: SearchErrorCallback;
  timeout?: number;
}

/**
 * Класс для работы с поиском через сокеты
 */
class SearchSocket {
  private socket: Socket | null = null;
  private readonly socketKey = 'search';
  private readonly trackError = trackApiError({
    sockets: 'search',
  });

  /**
   * Инициализирует сокет для поиска
   */
  public async connect(): Promise<Socket | null> {
    if (!isSocketAvailable()) {
      return null;
    }

    try {
      this.socket = createSocket(this.socketKey, {
        url: 'wss://my.idoctor.kz/',
        autoConnect: true,
      });

      return new Promise((resolve, reject) => {
        if (!this.socket) {
          reject(new Error('Не удалось создать сокет'));
          return;
        }

        if (this.socket.connected) {
          resolve(this.socket);
          return;
        }

        const timeout = setTimeout(() => {
          reject(new Error('Таймаут подключения к поиску'));
        }, 10000);

        this.socket.on('connect', () => {
          clearTimeout(timeout);
          resolve(this.socket);
        });

        this.socket.on('connect_error', error => {
          clearTimeout(timeout);
          reject(error);
        });
      });
    } catch (error) {
      this.trackError({
        status: 0,
        endpoint: process.env.NEXT_PUBLIC_SEARCH_URL!,
        method: 'connect',
        message: `Socket Error: ${error}`,
      });

      return null;
    }
  }

  /**
   * Выполняет type-safe поисковый запрос
   */
  public search<T extends SearchIndex[]>(body: SearchRequestBody<T>, options: SearchOptions<T> = {}): void {
    if (!this.socket || !this.socket.connected) {
      this.connect().then(socket => {
        if (socket) {
          this.performSearch(body, options);
        } else if (typeof options.onError === 'function') {
          options.onError('Не удалось подключиться к поиску');
        }
      });
      return;
    }

    this.performSearch(body, options);
  }

  /**
   * Внутренний метод выполнения поиска
   */
  private performSearch<T extends SearchIndex[]>(body: SearchRequestBody<T>, options: SearchOptions<T>): void {
    if (!this.socket) return;

    const { timeout = 30000 } = options;

    const timeoutId = setTimeout(() => {
      this.socket?.off('search_result');
      this.socket?.off('search_error');

      if (typeof options.onError === 'function') {
        options.onError('Таймаут поискового запроса');
      }
    }, timeout);

    this.socket.once('search_result', (response: SearchApiResponse<T>) => {
      clearTimeout(timeoutId);

      if (typeof options.onSuccess === 'function') {
        const adaptedResponse = adaptSearchResponse(response);
        options.onSuccess(adaptedResponse);
      }
    });

    this.socket.once('search_error', (error: unknown) => {
      clearTimeout(timeoutId);

      if (typeof options.onError === 'function') {
        options.onError(error);
      }
    });

    this.socket.emit('search', body);
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

/**
 * Адаптер для аватарок врачей в результатах поиска
 */
function adaptSearchedDoctors(doctors: SearchedDoctors[]): SearchedDoctors[] {
  if (process.env.NEXT_PUBLIC_ENV === 'prod') return doctors;

  return doctors.map(doctor => ({
    ...doctor,
    avatar:
      doctor.avatar && ['dev', 'local'].includes(process.env.NEXT_PUBLIC_ENV)
        ? `https://idoctor.kz/${doctor.avatar}`
        : doctor.avatar,
  }));
}

/**
 * Адаптер для аватарок медцентров в результатах поиска
 */
function adaptSearchedMedcenters(medcenters: SearchedMedcenters[]): SearchedMedcenters[] {
  if (process.env.NEXT_PUBLIC_ENV === 'prod') return medcenters;

  return medcenters.map(medcenter => ({
    ...medcenter,
    avatar:
      medcenter.avatar && ['dev', 'local'].includes(process.env.NEXT_PUBLIC_ENV)
        ? `https://idoctor.kz/${medcenter.avatar}`
        : medcenter.avatar,
  }));
}

/**
 * Адаптер для результатов поиска
 */
function adaptSearchResponse<T extends SearchIndex[]>(response: SearchApiResponse<T>): SearchApiResponse<T> {
  const adaptedData = { ...response.data };

  // Адаптируем врачей если они есть в результатах
  if (adaptedData && typeof adaptedData === 'object' && 'doctors' in adaptedData) {
    const doctorsData = adaptedData.doctors;
    if (doctorsData && typeof doctorsData === 'object' && 'data' in doctorsData) {
      adaptedData.doctors = {
        ...doctorsData,
        data: adaptSearchedDoctors(doctorsData.data as SearchedDoctors[]),
      };
    }
  }

  // Адаптируем медцентры если они есть в результатах
  if (adaptedData && typeof adaptedData === 'object' && 'medcenters' in adaptedData) {
    const medcentersData = adaptedData.medcenters;
    if (medcentersData && typeof medcentersData === 'object' && 'data' in medcentersData) {
      adaptedData.medcenters = {
        ...medcentersData,
        data: adaptSearchedMedcenters(medcentersData.data as SearchedMedcenters[]),
      };
    }
  }

  return {
    ...response,
    data: adaptedData as SearchDataResponse<T>,
  };
}

const searchSocket = new SearchSocket();

export type {
  SearchIndexToType,
  SearchDataResponse,
  SearchRequestBody,
  SearchApiResponse,
  SearchCallback,
  SearchErrorCallback,
  SearchOptions,
};

export { SearchSocket, searchSocket };
